import { useState } from 'react';

import CustomSelect from '@ui/select/custom-select/custom-select';
import { TReactSelectOption } from '@ui/select/react-select/custom-react-select.types';
import Text from '@ui/text/text/text';
import OptionalComponent from '@ui/optional-component/optional-component';
import { useAppSelector } from '@/store/hooks';
import { selectProjectGlobalTemplateType } from '@/store/slices/project/selectors';
import useFetchGlobalTemplateTypes from './use-fetch-global-template-types';

type GlobalTemplateTypeSelectorProps = {
  bypassFetchGlobalTemplateTypes?: boolean;
  onChange?: (globalTemplateType: string) => void;
  onMenuClose?: (globalTemplateType: string) => void;
  error?: string | null;
};

const GlobalTemplateTypeSelector = ({
  bypassFetchGlobalTemplateTypes,
  error,
  onChange,
  onMenuClose,
}: GlobalTemplateTypeSelectorProps) => {
  const workingProjectGlobalTemplateType = useAppSelector(selectProjectGlobalTemplateType);

  const [globalTemplateType, setGlobalTemplateType] = useState<TReactSelectOption | null>(
    workingProjectGlobalTemplateType
      ? { label: workingProjectGlobalTemplateType, value: workingProjectGlobalTemplateType }
      : null
  );

  const {
    fetching: fetchingGlobalTemplateTypes,
    globalTemplateTypes,
    error: globalTemplatesFetchError,
    setRefetch: setRefetchGlobalTemplates,
  } = useFetchGlobalTemplateTypes({ bypass: bypassFetchGlobalTemplateTypes });

  const handleChange = (options: TReactSelectOption[]) => {
    const next = options[0] || null;
    setGlobalTemplateType(next);
    onChange?.(next ? String(next.value) : '');
  };

  const handleEnterKeyPress = (inputValue: string, { hasActiveMenuOption }: { hasActiveMenuOption: boolean }) => {
    if (hasActiveMenuOption) return;

    const trimmed = inputValue.trim();
    if (!trimmed) return;

    const next: TReactSelectOption = { label: trimmed, value: trimmed };
    setGlobalTemplateType(next);
    onChange?.(trimmed);
  };

  const handleMenuClose = () => {
    if (globalTemplatesFetchError) {
      setRefetchGlobalTemplates((prev) => prev + 1);
    }
    onMenuClose?.(globalTemplateType ? String(globalTemplateType.value) : '');
  };

  const showError = !!error;
  const options: TReactSelectOption[] = globalTemplateTypes.map((t) => ({ label: t, value: t }));

  return (
    <div>
      <label className="margin-b-5 margin-l-16 P2" htmlFor="global-template-type-select">
        Global Template Type *
      </label>
      <Text className="S3 margin-b-5 margin-l-16" design="GlobalIcoText1">
        Visible to users when previewing projects in project details. Recommended to set similar to the project name —
        e.g. if the project name is &quot;Geode Webinar&quot;, set this to &quot;Geode&quot;.
      </Text>

      <CustomSelect
        id="global-template-type-select"
        hideHeader
        labelHeight="48px"
        Placeholder="Global templates type (type to create new)"
        options={options}
        controlledOptions={globalTemplateType ? [globalTemplateType] : []}
        onChange={handleChange}
        onEnterKeyPress={handleEnterKeyPress}
        onMenuClose={handleMenuClose}
        isMultiSelect={false}
        selectFirstSearchOptionOnEnter={false}
        permanentDropdownIcon
        clearInputOnMouseSelect
        clearInputOnEnter
        disabled={fetchingGlobalTemplateTypes}
        status={showError ? 'error' : null}
        RenderNoOptionsContent={({ inputValue }) => (
          <Text className="P2 padding-12" color={globalTemplatesFetchError ? 'var(--semantic-red-500)' : undefined}>
            {globalTemplatesFetchError ||
              (inputValue?.trim() ? `Press enter to create ${inputValue.trim()}` : 'No options')}
          </Text>
        )}
      />

      <OptionalComponent display={showError}>
        <Text className="S3 margin-l-16" color="var(--semantic-red-500)">
          {error}
        </Text>
      </OptionalComponent>
    </div>
  );
};

export default GlobalTemplateTypeSelector;
